import { Event } from "../lib/types";
import { User, NotificationPreferences } from "../contexts/auth/types";
import { getRecommendedEvents } from "../services/notificationService";
import { calculateProfessionMatchScore } from "../services/professionService";

/**
 * Get recommended events for a user based on their notification preferences
 */
export const getPersonalizedEvents = (
  allEvents: Event[],
  user: User | null
): Event[] => {
  if (!user || !user.userProfile.notificationPreferences?.preferredTopics?.length) {
    console.log("No personalized events: missing user or preferences");
    return [];
  }

  console.log(`Getting personalized events for user with ${user.userProfile.notificationPreferences.preferredTopics.length} topics`);

  try {
    const events = getRecommendedEvents(allEvents, user.userProfile.notificationPreferences)
      .filter((event: Event | null) => event !== null) // Filter out null values
      .map((event: Event) => {
        // Calculate profession match score
        const score = calculateProfessionMatchScore(
          event.professions || [],
          (user.userProfile.notificationPreferences?.preferredTopics || []).map((topic: string) => ({
            professionId: topic,
            level: 'high'
          }))
        );

        return {
          ...event,
          isRecommended: true,
          matchScore: score
        };
      })
      .sort((a, b) => ((b?.matchScore || 0) - (a?.matchScore || 0)));

    console.log(`Found ${events.length} personalized events`);
    return events;
  } catch (error) {
    console.error("Error getting personalized events:", error);
    return [];
  }
};

/**
 * Add 'isRecommended' flag to events that match user preferences
 */
export const markRecommendedEvents = (
  events: Event[],
  user: User | null
): Event[] => {
  if (!user || !user.userProfile.notificationPreferences?.preferredTopics?.length) {
    return events;
  }

  try {
    const recommendedEventIds = getRecommendedEvents(events, user.userProfile.notificationPreferences)
      .filter((event: Event) => event && event.id)
      .map((event: Event) => event.id);

    return events.map((event: Event) => ({
      ...event,
      isRecommended: Boolean(event.id && recommendedEventIds.includes(event.id))
    }));
  } catch (error) {
    console.error("Error marking recommended events:", error);
    return events;
  }
};
